import { csvRowToArray } from 'utils/array';

import type { ChatWithDataFileData } from './types';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

export async function parseCsvFile(file: File): Promise<ChatWithDataFileData> {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error('File is too large. Maximum size is 10 MB.');
  }

  const text = await readFileAsText(file);
  const lines = text.split('\n');
  const columns = csvRowToArray(lines[0].replace('\r', '').trim()).map((col) => col.trim());

  if (columns.length === 0 || columns.every((c) => c === '')) {
    throw new Error('No columns found in header row.');
  }

  const filteredLines = lines
    .slice(1)
    .map((line) => line.trim())
    .filter((s) => s !== '');

  if (filteredLines.length === 0) {
    throw new Error('File has no data rows.');
  }

  const rows: (string | number | boolean | null)[][] = [];
  for (const [rowIndex, line] of filteredLines.entries()) {
    const cells = csvRowToArray(line);
    if (cells.length !== columns.length) {
      throw new Error(`Row ${rowIndex + 2} has ${cells.length} fields but the header has ${columns.length}.`);
    }
    rows.push(cells.map((c) => c.trim() || null));
  }

  return { fileName: file.name, columns, rows };
}

export const ACCEPTED_FILE_EXTENSIONS = '.csv';
